import * as ngCore from '@angular/core'
import { outputToObservable } from '@angular/core/rxjs-interop'
import * as ents from '@attain-apps/entities'
import * as ds from '@attain-apps/fe/web/main/lib/design'
import { TypeAsserter } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'
import * as rxjs from 'rxjs'

import * as state from '../../../../../../state'

/**
 * Dialog for uploading DMS data files.
 */
@ngCore.Component({
  imports: [
    ds.button.Component,
    ds.input.checkbox.Component,
    ds.input.file.Component,
    ds.layout.widget.Component,
  ],
  selector: 'atn-upload-data-dialog',
  standalone: true,
  styleUrls: ['./upload-data-dialog.component.scss'],
  templateUrl: './upload-data-dialog.component.html',
})
export class AtnUploadDataDialogComponent extends ComponentStore<State> {
  /** @see {@link state.fileMgmt.Api} */
  private readonly _fileMgmtApi = ngCore.inject(state.fileMgmt.Api)

  /** @see {@link state.me.Feature} */
  public readonly meFeature = ngCore.inject(state.me.Feature)

  /** @see {@link state.relay.processes.Feature} */
  private readonly _processesFeature = ngCore.inject(
    state.relay.processes.Feature,
  )

  /** @see {@link state.display.toaster.Feature} */
  private readonly _toasterFeature = ngCore.inject(
    state.display.toaster.Feature,
  )

  /** File if its loaded or undefined */
  public readonly fileSg = this.selectSignal(({ file }) => file)

  /** Current file state */
  public readonly fileStateSg = this.selectSignal(({ fileState }) => fileState)

  /** Whether upload should be marked as replacing existing data */
  public readonly replaceExistingDataSg = this.selectSignal(
    ({ replaceExistingData }) => replaceExistingData,
  )

  /** Emits dialog cancel event */
  public readonly cancel = ngCore.output<void>()

  /** Emits upload start event */
  public readonly uploadStart = ngCore.output<File>()

  /** Emits upload finished event */
  public readonly uploadFinish = ngCore.output<File>()

  constructor() {
    super(State.INITIAL)
  }

  /** Handler to set file loaded to browser */
  public fileChangeHandler(file: File | undefined): void {
    this.patchState({ file })
  }

  /** Handler to set replace-upload option */
  public replaceExistingDataChangeHandler(replaceExistingData: boolean): void {
    this.patchState({ replaceExistingData })
  }

  // TODO: service stuff should be moved into, consuming part so this component can be reused
  /** Handler to upload file */
  public uploadButtonClickHandler(): void {
    const file = TypeAsserter.validate(
      this.fileSg(),
      TypeAsserter.assertIsNotNullish,
    )
    this.uploadStart.emit(file)
    this.patchState({ fileState: 'uploading' })
    this._fileMgmtApi
      .getUploadRequest({
        context: TypeAsserter.validate(
          this.meFeature.contexts.selectors.zoneContext.asSignal(),
          TypeAsserter.assertIsNotNullish,
        ),
        file,
        fileName: file.name,
        purpose: ents.core.common.fileManagement.Purpose.DmsUpload,
        qualifier: this.replaceExistingDataSg()
          ? ents.core.common.fileManagement.DmsUpload
              .REPLACE_EXISTING_DATA_QUALIFIER
          : undefined,
      })
      .pipe(
        rxjs.first(),
        rxjs.takeUntil(outputToObservable(this.cancel)),
        // Shows a sticky toast notification when a file upload is triggered,
        // indicating that a data import process is in progress.
        // The toast provides a button to open the processes slideout on demand.
        rxjs.tap(() =>
          this._toasterFeature.actions.show.from('UploadFileRequest').dispatch({
            toast: {
              actions: [
                {
                  callback: () => {
                    this._processesFeature.actions.openSlideout
                      .from('UploadFileRequest')
                      .dispatch()
                  },
                  closeOnClick: true,
                  id: 'view-processes',
                  kind: 'button',
                  name: 'View processes',
                },
              ],
              description: 'Your data is being processed.',
              durationMs: null,
              name: 'Import started',
            },
          }),
        ),
      )
      .subscribe(() => this.uploadFinish.emit(file))
  }
}

interface State {
  file: File | undefined
  fileState: 'idle' | 'uploading'
  replaceExistingData: boolean
}
namespace State {
  export const INITIAL: State = {
    file: undefined,
    fileState: 'idle',
    replaceExistingData: false,
  }
}
